import { useDeleteNode } from "@/api/workflow-hooks";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbSeparator,
} from "@/components/breadcrumb";
import { Button } from "@/components/button";
import { ACTIONS_CONFIG } from "@/pages/Projects/Editor/Actions/shared";
import { TrashIcon } from "@radix-ui/react-icons";
import type { ActionNode } from "@repo/shared/workflow";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import ActionEditor from "./ActionEditor";
import { CreateActionDialog } from "./CreateActionDialog";

const Actions: React.FC<{
  workflowId: string;
  actionNodes: ActionNode[];
  selectedActionId: string | null;
  actionGroupNodeId: string;
}> = ({
  workflowId,
  actionNodes,
  selectedActionId: actionId,
  actionGroupNodeId,
}) => {
  const deleteNodeMut = useDeleteNode(workflowId);

  const [isCreateActionDialogOpen, setIsCreateActionDialogOpen] =
    useState(false);

  const action = useMemo(() => {
    return actionNodes?.find((a) => a.id === actionId);
  }, [actionNodes, actionId]);

  return (
    <div className="h-[calc(100dvh-4rem)] overflow-auto">
      <div className="">
        <div className="border-b py-2 px-4 flex items-center justify-between sticky top-0 bg-background z-10">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem className="text-base text-foreground font-medium">
                <Link to={`/projects/${workflowId}/actions`}>Actions</Link>
              </BreadcrumbItem>

              {actionId && (
                <>
                  <BreadcrumbSeparator />
                  <BreadcrumbItem>{action?.name}</BreadcrumbItem>
                </>
              )}
            </BreadcrumbList>
          </Breadcrumb>
          <CreateActionDialog
            workflowId={workflowId}
            actionGroupNodeId={actionGroupNodeId}
            open={isCreateActionDialogOpen}
            onClose={() => setIsCreateActionDialogOpen(false)}
            onOpenChange={setIsCreateActionDialogOpen}
          />
        </div>
        <div>
          {action && <ActionEditor workflowId={workflowId} action={action} />}
        </div>
      </div>
      {!actionId && (
        <div className="flex flex-col divide-y border-b">
          {actionNodes?.map((action) => (
            <Link
              key={action.id}
              to={`/projects/${workflowId}/actions/${action.id}`}
            >
              <div className="cursor-pointer hover:bg-secondary/40 grid grid-cols-[40px,1fr,40px] py-2 px-4 group">
                <img
                  src={ACTIONS_CONFIG[action.subType].icon}
                  className="w-10 h-10"
                  alt={ACTIONS_CONFIG[action.subType].label}
                />
                <div className="h-full flex items-center px-4">
                  {action.name}
                </div>
                <Button
                  variant="ghost"
                  onClick={(e) => {
                    e.preventDefault();
                    deleteNodeMut.mutate({
                      nodeId: action.id,
                    });
                  }}
                  className="group-hover:opacity-100 opacity-0"
                >
                  <TrashIcon className="w-4 h-4" />
                </Button>
              </div>
            </Link>
          ))}
          {actionNodes?.length === 0 && (
            <div className="border m-4 p-4 h-48 flex items-center bg-secondary rounded-lg justify-center flex-col">
              <div className="text-sm mb-4 text-gray-500">No actions yet!</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Actions;
